import { computed, inject, Injectable } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { AuthService } from "@auth0/auth0-angular";
import { OnboardingOrchestratorService, OrganizationStatusService } from "@kno2/features/onboarding";
import { noop } from "@kno2/utils";
import { IntercomService } from "./core/comms";
import { SessionService } from "./core/session";
import { UserProfile } from "./core/session/user-profile.model";
import { LocalStorageService, migrateLegacyCdaPreferences } from "./core/storage";
import { ApplicationInsightsService } from "./core/telemetry";
import { Brand } from "./data-access/brands";
import { IntegrationsService } from "./data-access/integrations";
import { RulesService } from "./data-access/rules";
import { SettingsService } from "./data-access/settings";
import { UsersService } from "./data-access/users";
import { FeaturesService } from "./shared/features";
import { SessionTimeoutService } from "./shared/session-timout";

@Injectable({
    providedIn: "root"
})
export class StartupService {
    private readonly authService = inject(AuthService);
    private readonly sessionService = inject(SessionService);
    private readonly usersService = inject(UsersService);
    private readonly applicationInsightsService = inject(ApplicationInsightsService);
    private readonly featuresService = inject(FeaturesService);
    private readonly settingsService = inject(SettingsService);
    private readonly localStorageService = inject(LocalStorageService);
    private readonly intercomService = inject(IntercomService);
    private readonly integrationsService = inject(IntegrationsService);
    private readonly rulesService = inject(RulesService);
    private readonly sessionTimeoutService = inject(SessionTimeoutService);

    private readonly onboardingOrchestratorService = inject(OnboardingOrchestratorService);
    private readonly organizationStatusService = inject(OrganizationStatusService);

    private readonly isAuthenticated = toSignal(this.authService.isAuthenticated$);
    private readonly profileData = this.sessionService.profileData;

    public isReady = computed(() => this.isAuthenticated() && !!this.profileData() && this.sessionService.profileConfirmed());

    public async start(): Promise<void> {
        this.sessionTimeoutService.start();
        migrateLegacyCdaPreferences();

        await this.featuresService.load({ reload: true });
        const { userId, organizationId, brandId, organizationName } = this.profileData()!;

        this.loadBrandSettings(brandId);
        this.loadUserPreferences(userId);

        if (this.sessionService.userInRole(["Administrator", "User"])) {
            this.loadInteroperabilitySettings();
            this.loadIntegrationCapabilities();
            this.loadRules();
            this.loadIntakeRules(userId);
        }

        if (this.sessionService.userInRole("Administrator")) {
            this.loadDownloadProfileSources(userId);
        }

        this.applicationInsightsService.setContextUserId(userId);
        this.applicationInsightsService.setAppInsightsCustomFields<string>({
            organizationId,
            organizationName
        });
        this.intercomService.boot();

        await this.onboardingOrchestratorService.initializeIdentityProofingWorkflow();
        await this.organizationStatusService.initializeOrganizationStatusWorkflow();
    }

    private async loadUserPreferences(userId: UserProfile["userId"]): Promise<void> {
        const user = await this.usersService.getUserAsync(userId);

        this.localStorageService.set("userPreferences", user.userPreferences);
    }

    private loadBrandSettings = (brandId: Brand["id"]) => this.settingsService.getBrandSettingsAsync(brandId);

    private loadInteroperabilitySettings = () => this.settingsService.getInteroperabilitySettingsAsync().catch(noop);

    private loadIntegrationCapabilities = () => this.integrationsService.getCapabilitiesAsync().catch(noop);

    private loadRules = () => this.rulesService.getRulesAsync().catch(noop);

    private loadIntakeRules = (userId: UserProfile["userId"]) => this.usersService.getIntakeRulesAsync(userId).catch(noop);

    private loadDownloadProfileSources = (userId: UserProfile["userId"]) => this.usersService.getDownloadProfileDocumentSources(userId).catch(noop);
}
